import React from 'react';
import { 
  Activity, 
  Clock, 
  CheckCircle2, 
  User, 
  AlertCircle, 
  ChevronRight,
  HeartPulse
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { ChartContainer } from './ChartContainer';

const riskData = [
  { name: 'Low Risk', value: 45, color: '#10b981' },
  { name: 'Moderate Risk', value: 30, color: '#f59e0b' },
  { name: 'High Risk', value: 15, color: '#ef4444' },
  { name: 'Critical', value: 10, color: '#7f1d1d' },
];

export const Dashboard: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-2 font-medium">
            <span>Cardio-Onc AI</span>
            <span>/</span>
            <span className="text-raspberry-500 uppercase">Dashboard</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Clinical Dashboard</h2>
          <p className="text-slate-500 text-sm md:text-base">Real-time cardio-oncology oversight and AI decision support.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <span className="text-xs md:text-sm text-slate-500 font-medium">Last updated: Just now</span>
          <button className="w-full sm:w-auto bg-raspberry-600 hover:bg-raspberry-700 text-white px-5 py-2.5 rounded-lg font-medium transition-colors shadow-sm">
            New Consultation
          </button>
        </div>
      </header>

      {/* Top Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 md:gap-5">
          <div className="text-raspberry-500">
            <Activity size={28} strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs md:text-sm font-medium text-slate-500 mb-1">Active Protocols</p>
            <p className="text-2xl md:text-3xl font-bold text-slate-900">12</p>
          </div>
        </div>
        <div className="bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 md:gap-5">
          <div className="text-amber-500">
            <Clock size={28} strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs md:text-sm font-medium text-slate-500 mb-1">Pending Review</p>
            <p className="text-2xl md:text-3xl font-bold text-slate-900">3</p>
          </div>
        </div>
        <div className="bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4 md:gap-5 sm:col-span-2 lg:col-span-1">
          <div className="text-emerald-500">
            <CheckCircle2 size={28} strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs md:text-sm font-medium text-slate-500 mb-1">Compliant Tasks</p>
            <p className="text-2xl md:text-3xl font-bold text-slate-900">98%</p>
          </div>
        </div>
      </div>

      {/* Middle Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Cases */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 md:p-6 border-b border-slate-100 flex justify-between items-center">
            <h3 className="font-bold text-lg text-slate-900">Recent Cases Oversight</h3>
            <a href="#" className="text-raspberry-600 text-sm font-medium flex items-center hover:underline">
              View all <ChevronRight size={16} className="ml-1" />
            </a>
          </div>
          <div className="divide-y divide-slate-100 flex-1">
            <div className="p-4 md:p-5 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-raspberry-50 text-raspberry-600 flex items-center justify-center border border-raspberry-100 shrink-0">
                  <User size={18} />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 text-sm md:text-base truncate">Case #8291</p>
                  <p className="text-[10px] md:text-[11px] font-bold text-slate-400 tracking-wider mt-0.5 uppercase">Interpretation</p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="inline-block px-2 py-0.5 md:px-2.5 md:py-1 bg-amber-100 text-amber-800 text-[10px] md:text-xs font-bold rounded-md mb-1">
                  Requires Review
                </span>
                <p className="text-[10px] md:text-xs text-slate-400 font-medium">12 mins ago</p>
              </div>
            </div>
            <div className="p-4 md:p-5 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-raspberry-50 text-raspberry-600 flex items-center justify-center border border-raspberry-100 shrink-0">
                  <User size={18} />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 text-sm md:text-base truncate">Case #7712</p>
                  <p className="text-[10px] md:text-[11px] font-bold text-slate-400 tracking-wider mt-0.5 uppercase">Consult</p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="inline-block px-2 py-0.5 md:px-2.5 md:py-1 bg-emerald-100 text-emerald-800 text-[10px] md:text-xs font-bold rounded-md mb-1">
                  Completed
                </span>
                <p className="text-[10px] md:text-xs text-slate-400 font-medium">1 hour ago</p>
              </div>
            </div>
            <div className="p-4 md:p-5 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer">
              <div className="flex items-center gap-3 md:gap-4">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-raspberry-50 text-raspberry-600 flex items-center justify-center border border-raspberry-100 shrink-0">
                  <User size={18} />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-slate-900 text-sm md:text-base truncate">Case #9023</p>
                  <p className="text-[10px] md:text-[11px] font-bold text-slate-400 tracking-wider mt-0.5 uppercase">Risk</p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="inline-block px-2 py-0.5 md:px-2.5 md:py-1 bg-red-100 text-red-800 text-[10px] md:text-xs font-bold rounded-md mb-1">
                  High Risk Alert
                </span>
                <p className="text-[10px] md:text-xs text-slate-400 font-medium">3 hours ago</p>
              </div>
            </div>
          </div>
        </div>

        {/* Risk Distribution */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-[400px]">
          <h3 className="font-bold text-lg text-slate-900 mb-6">Risk Distribution</h3>
          <ChartContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
              <span className="text-2xl md:text-3xl font-bold text-slate-900">85%</span>
              <span className="text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-wider">Compliance</span>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={riskData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={75}
                  paddingAngle={4}
                  dataKey="value"
                  stroke="none"
                  cornerRadius={4}
                >
                  {riskData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
          <div className="grid grid-cols-2 gap-y-3 mt-6 px-2">
            {riskData.map((item) => (
              <div key={item.name} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }}></div>
                <span className="text-[10px] md:text-xs font-medium text-slate-600 truncate">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-raspberry-900 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden shadow-md">
          <div className="absolute -right-12 -bottom-12 opacity-10 pointer-events-none text-white">
            <HeartPulse size={200} strokeWidth={1} />
          </div>
          <div className="relative z-10">
            <h3 className="text-lg md:text-xl font-bold mb-3">Protocol Reminder</h3>
            <p className="text-raspberry-200 mb-6 max-w-xl leading-relaxed text-sm md:text-base">
              ASCO/ESMO guidelines recommend baseline echocardiography and biomarker assessment (Troponin/BNP) for all patients starting anthracycline therapy or anti-HER2 targeted agents.
            </p>
            <button className="bg-white text-raspberry-900 px-5 py-2.5 rounded-lg font-bold text-sm hover:bg-raspberry-50 transition-colors">
              View Protocol Library
            </button>
          </div>
        </div>
        <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200 shadow-sm flex flex-col justify-center">
          <div className="flex items-start gap-3">
            <div className="text-amber-500 mt-0.5">
              <AlertCircle size={24} strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-bold text-amber-900 mb-1.5">Compliance Audit Due</h3>
              <p className="text-sm text-amber-700 leading-relaxed">
                Monthly AI governance audit requires manual sign-off for 3 cases.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
